import { useState } from 'react'
import { Figure } from '@/components/Figure'
import { getExercise } from '@/data/exercises'
import type { Phase, Workout } from '@/domain/workout'
import { phaseDuration } from '@/domain/timeline'
import { cx } from '@/lib/cx'
import { mmss } from '@/lib/format'
import styles from './panel.module.css'

/** Una fila de la ficha: dosis, nombre y monigote. */
function MoveRow({ exerciseId, dose }: { exerciseId: string; dose: string }) {
  const exercise = getExercise(exerciseId)
  return (
    <div className={styles.move}>
      <span className={styles.moveReps}>{dose}</span>
      <div className={styles.moveText}>
        <div className={styles.moveName}>{exercise.name}</div>
        <div className={styles.moveEs}>{exercise.nameEs}</div>
      </div>
      <div className={styles.moveFigure}>
        <Figure exerciseId={exerciseId} />
      </div>
    </div>
  )
}

function PhaseRows({ phase }: { phase: Phase }) {
  switch (phase.kind) {
    case 'intervals': {
      // El descanso al cerrar la ronda solo se anuncia si de verdad es otro:
      // si coincide con el de siempre, repetirlo solo añade ruido.
      const roundRest = phase.roundRestSeconds ?? phase.restSeconds
      return (
        <>
          {phase.pattern.map((movement, index) => (
            <MoveRow key={index} exerciseId={movement.exerciseId} dose={`${movement.seconds}"`} />
          ))}
          <div className={styles.move}>
            <span className={styles.moveReps}>{phase.restSeconds}&quot;</span>
            <div className={styles.moveText}>
              <div className={styles.moveName}>Descanso entre ejercicios</div>
              <div className={styles.moveEs}>{phase.rounds} rondas completas</div>
            </div>
          </div>
          {roundRest !== phase.restSeconds && (
            <div className={styles.move}>
              <span className={styles.moveReps}>{roundRest}&quot;</span>
              <div className={styles.moveText}>
                <div className={styles.moveName}>Descanso entre rondas</div>
                <div className={styles.moveEs}>Al cerrar cada vuelta</div>
              </div>
            </div>
          )}
        </>
      )
    }
    case 'amrap':
      return (
        <>
          {phase.movements.map((movement, index) => (
            <MoveRow key={index} exerciseId={movement.exerciseId} dose={String(movement.reps)} />
          ))}
        </>
      )
    case 'afap':
      return (
        <>
          {phase.blocks.map((movement, index) => (
            <MoveRow key={index} exerciseId={movement.exerciseId} dose={String(movement.reps)} />
          ))}
        </>
      )
    case 'countdown':
      return (
        <div className={styles.move}>
          <span className={styles.moveReps}>{mmss(phase.durationSeconds)}</span>
          <div className={styles.moveText}>
            <div className={styles.moveName}>{phase.briefing ?? phase.summary}</div>
          </div>
        </div>
      )
  }
}

/** El detalle del entreno, plegado por bloques. */
export function PhaseList({ workout }: { workout: Workout }) {
  const [openId, setOpenId] = useState<string | null>(null)

  return (
    <div>
      {workout.phases.map((phase, index) => {
        const open = openId === phase.id
        return (
          <article key={phase.id} className={styles.phase}>
            <button
              type="button"
              className={styles.phaseButton}
              aria-expanded={open}
              onClick={() => setOpenId(open ? null : phase.id)}
            >
              <span className={styles.phaseIndex}>{String(index + 1).padStart(2, '0')}</span>
              <span className={styles.phaseText}>
                <span className={styles.phaseName}>{phase.name}</span>
                <span className={styles.phaseSummary}>{phase.summary}</span>
              </span>
              <span className={styles.phaseDuration}>
                {phase.kind === 'afap' ? 'AFAP' : mmss(phaseDuration(phase))}
              </span>
              <span className={cx(styles.chevron, open && styles.chevronOpen)} />
            </button>
            {open && (
              <div className={styles.phaseBody}>
                <PhaseRows phase={phase} />
              </div>
            )}
          </article>
        )
      })}
    </div>
  )
}
